import { useCallback, useRef, useState } from 'react';
import { joinWithAnd } from '../../helpers/utils';
import { findMissingBotIds } from '../../helpers/multiCharacter';

/**
 * Which character is doing what in a multi-character session.
 *
 * - active speaker: the bot the agent is generating for, from
 *   `speaker_started` until `speaker_ended`. Events that omit `bot_id` are
 *   attributed to it.
 * - talking bot: the bot whose audio chunk is playing right now. Audio trails
 *   the text stream, so the next speaker can start while the previous one is
 *   still talking; the mouth follows the audio, not the speaker events.
 * - emotions: the last expression each bot sent, held until it sends another.
 */
function useMultiCharacterSpeakerState() {
  // Read synchronously from the socket handler, so a ref rather than state.
  const activeSpeakerBotIdRef = useRef(null);
  const [talkingBotId, setTalkingBotId] = useState(null);
  const [emotionsByBotId, setEmotionsByBotId] = useState({});

  const registerSessionAgents = useCallback((sessionAgents, expectedBotIds) => {
    const missingBotIds = findMissingBotIds(expectedBotIds, sessionAgents);

    if (missingBotIds.length > 0) {
      console.warn(
        '[Nudgyt Agent Multi-Character Client] Not in the session agents:',
        joinWithAnd(missingBotIds)
      );
    }
  }, []);

  const startSpeaker = useCallback((botId) => {
    activeSpeakerBotIdRef.current = botId ?? null;
  }, []);

  const endSpeaker = useCallback((botId) => {
    if (!botId || activeSpeakerBotIdRef.current === botId) {
      activeSpeakerBotIdRef.current = null;
    }
  }, []);

  const resolveSpeakerBotId = useCallback(
    (botId) => botId ?? activeSpeakerBotIdRef.current,
    []
  );

  const setSpeakerEmotion = useCallback((botId, emotion) => {
    if (!botId || !emotion || typeof emotion !== 'object') return;

    setEmotionsByBotId((previous) => ({ ...previous, [botId]: emotion }));
  }, []);

  return {
    talkingBotId,
    emotionsByBotId,
    setTalkingBotId,
    registerSessionAgents,
    startSpeaker,
    endSpeaker,
    resolveSpeakerBotId,
    setSpeakerEmotion
  };
}

export default useMultiCharacterSpeakerState;
